'use client';

import { ChevronDown, ChevronUp, AlertTriangle, Shield } from 'lucide-react';

export interface AirspaceTfrItem {
  id?: string;
  name?: string;
  notam_id?: string;
  tfr_type?: string;
  effective_from?: string;
  effective_to?: string;
  alt_low_ft?: number;
  alt_high_ft?: number;
}

export interface AirspaceNotamItem {
  id: string;
  text: string;
  type: string;
  severity: 'notice' | 'warning' | 'critical';
  latitude: number;
  longitude: number;
}

interface AirspacePanelProps {
  visible: boolean;
  loading: boolean;
  error: string | null;
  tfrs: AirspaceTfrItem[];
  notams: AirspaceNotamItem[];
  /** From API NOTAM summary */
  notamClearToFly: boolean;
  /** Combined COP assessment */
  restrictionsActive: boolean;
  /** Chrome floating over the map's bottom edge, in CSS px. Zero in the classic shell. */
  bottomInsetPx?: number;
  /**
   * Chrome floating over the map's LEFT edge, in CSS px.
   *
   * `left-3` puts this against the stage's left edge, which the floating
   * navigation rail covers. Zero when nothing floats there.
   */
  leftInsetPx?: number;
}

function fmtUtc(iso: string | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().replace('T', ' ').slice(0, 16) + ' UTC';
}

export function AirspacePanel({
  visible,
  loading,
  error,
  tfrs,
  notams,
  notamClearToFly,
  restrictionsActive,
  bottomInsetPx = 0,
  leftInsetPx = 0,
}: AirspacePanelProps) {
  if (!visible) return null;

  return (
    <div
      // The monospace utility is gone from this container: T1 (#519, D17) moved
      // it off the chrome and onto the numbers themselves. Named indirectly on
      // purpose - `typography-rule.test.ts` scans this file as plain text, so
      // spelling the class out here would report the comment as an offender.
      className="absolute bottom-11 left-3 z-10 w-[min(calc(100%-1.5rem),22rem)] max-h-[40vh] flex flex-col rounded-md border border-border bg-popover/90 backdrop-blur-sm text-xs text-muted-foreground overflow-hidden"
      // Rides above whatever floats over the map's bottom edge, keeping its
      // clearance over the corner pill below it - the map key and scale bar
      // share one 28px pill now (they used to be a 44px pill stacked over a
      // 28px one, which is why this was bottom-22). Zero in the classic shell,
      // where `bottom-11` alone is right.
      style={{
        ...(bottomInsetPx > 0 ? { bottom: `calc(2.75rem + ${bottomInsetPx}px)` } : null),
        ...(leftInsetPx > 0 ? { left: `calc(0.75rem + ${leftInsetPx}px)` } : null),
      }}
    >
      <div className="px-3 py-2 border-b border-border flex items-center justify-between gap-2 shrink-0">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">FAA airspace</span>
        {loading && <span className="text-muted-foreground">Loading…</span>}
      </div>

      <div className="px-3 py-2 border-b border-border flex items-start gap-2 shrink-0">
        {restrictionsActive ? (
          <>
            <AlertTriangle className="size-4 text-status-amber-fg shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <div className="text-status-amber-fg">Restrictions active</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                Review TFRs and NOTAMs before flight. Demo data :  verify with official FAA sources.
              </div>
            </div>
          </>
        ) : (
          <>
            <Shield className="size-4 text-status-green-fg shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <div className="text-status-green-fg">Clear to fly (NOTAM check)</div>
              <div className="text-[10px] text-muted-foreground mt-0.5">
                No blocking NOTAMs in query radius. Always confirm TFRs and local rules.
              </div>
            </div>
          </>
        )}
      </div>

      {error && (
        <div className="px-3 py-2 text-status-red-fg border-b border-border shrink-0">{error}</div>
      )}

      <div className="overflow-y-auto flex-1 min-h-0">
        <details open className="group border-b border-border">
          <summary className="px-3 py-2 cursor-pointer flex items-center justify-between text-foreground hover:bg-black/[0.03] dark:hover:bg-white/5 list-none [&::-webkit-details-marker]:hidden">
            <span>TFRs ({tfrs.length})</span>
            <span className="text-muted-foreground group-open:hidden">
              <ChevronDown className="size-4" />
            </span>
            <span className="text-muted-foreground hidden group-open:inline">
              <ChevronUp className="size-4" />
            </span>
          </summary>
          <ul className="px-3 pb-2 space-y-2 text-[11px]">
            {tfrs.length === 0 && !loading && (
              <li className="text-muted-foreground">No active TFRs in demo dataset.</li>
            )}
            {tfrs.map((t) => (
              <li key={t.id ?? t.notam_id ?? t.name} className="border-l-2 border-status-red/60 pl-2">
                <div className="text-status-red-fg">{t.name ?? 'TFR'}</div>
                <div className="text-[10px] text-muted-foreground">
                  {t.tfr_type && <span className="uppercase">{t.tfr_type}</span>}
                  {t.notam_id && <span className="ml-1">{t.notam_id}</span>}
                </div>
                <div className="text-[10px] mt-0.5">
                  {t.alt_low_ft != null && t.alt_high_ft != null && (
                    <span className="font-mono tabular-nums">
                      {t.alt_low_ft}–{t.alt_high_ft} ft MSL
                    </span>
                  )}
                </div>
                <div className="text-[10px] font-mono tabular-nums text-muted-foreground mt-0.5">
                  {fmtUtc(t.effective_from)} → {fmtUtc(t.effective_to)}
                </div>
              </li>
            ))}
          </ul>
        </details>

        <details open className="group">
          <summary className="px-3 py-2 cursor-pointer flex items-center justify-between text-foreground hover:bg-black/[0.03] dark:hover:bg-white/5 list-none [&::-webkit-details-marker]:hidden">
            <span>
              NOTAMs ({notams.length}){notamClearToFly ? '' : ' · review'}
            </span>
            <span className="text-muted-foreground group-open:hidden">
              <ChevronDown className="size-4" />
            </span>
            <span className="text-muted-foreground hidden group-open:inline">
              <ChevronUp className="size-4" />
            </span>
          </summary>
          <ul className="px-3 pb-2 space-y-2 text-[11px]">
            {notams.length === 0 && !loading && (
              <li className="text-muted-foreground">None in 50 NM of map center (simulated).</li>
            )}
            {notams.map((n) => (
              <li
                key={n.id}
                className={
                  n.severity === 'critical'
                    ? 'border-l-2 border-status-red/70 pl-2'
                    : n.severity === 'warning'
                      ? 'border-l-2 border-status-amber/50 pl-2'
                      : 'border-l-2 border-input pl-2'
                }
              >
                <div className="text-foreground">{n.text}</div>
                <div className="text-[10px] text-muted-foreground uppercase mt-0.5">
                  {n.type} · {n.severity}
                </div>
              </li>
            ))}
          </ul>
        </details>
      </div>
    </div>
  );
}
